import React, { useCallback, useState } from 'react';
import { IonContent, IonPage, RefresherEventDetail, useIonViewWillEnter } from '@ionic/react';
import { Container, Refresher } from '@mindly/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { subscriptionsSelector } from '../../redux/subscriptions/selector';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { userSelector } from '../../redux/user/selector';
import { euroRate } from '../../redux/firestoreGlobalVariables/selector';
import { getFirestoreGlobalVariablesThunk } from '../../redux/firestoreGlobalVariables/action';
import { ISubscription } from '../../redux/subscriptions/types';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { FullWidthItem, SubscriptionCardContainer } from './SubscriptionControl.styled';
import { useHistory } from 'react-router';
import { PublicRoutes } from '../../routes/navigation';
import CancelSubscriptionModal from './CancelSubscriptionModal/CancelSubscriptionModal';
import { PauseModal } from './PauseModal';
import { UnpauseModal } from './UnpauseModal';
import { fetchChatRooms } from '../../redux/chatRooms/thunk';
import { resetOffset } from '../../redux/chatRooms/reducer';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import { useIntl } from 'react-intl';
import PageHeader from '../../components/PageHeader/PageHeader';
import SubscriptionCard from './SubscriptionCard/SubscriptionCard';
import SubscriptionSkeleton from './SubscriptionSkeleton';
import { SUBSCRIPTION_CONTROL, SUBSCRIPTION_WHAT_THERAPY_PLAN_IS } from '../../intercom-articles';
import useToast from '../../context/ToastContext/ToastContext';
import { useAnalytics } from 'src/shared';

const SubscriptionControl: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const { showToast } = useToast();
  const { trackPredefinedEvent } = useAnalytics();

  const [loading, setLoading] = useState(false);

  const [isCancelModalActive, setIsCancelModalActive] = useState(false);
  const [isPauseModalActive, setIsPauseModalActive] = useState(false);
  const [isUnpauseModalActive, setIsUnpauseModalActive] = useState(false);

  const [selectedSubscription, setSelectedSubscription] = useState<ISubscription | null>(null);

  const user = useSelector(userSelector);
  const subscriptions = useSelector(subscriptionsSelector());
  const EURToUAHRate = useSelector(euroRate);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    dispatch(setIsActive(true));
    dispatch(setActiveTab(Tabs.fifth));
  }, []);

  const openCancelFlowHandler = useCallback((subscription: ISubscription) => {
    trackPredefinedEvent('SUBSCRIPTION_CANCEL_SCREEN_VIEWED', {
      subscription_id: subscription.id,
      specialist_id: subscription.therapist_id,
    });
    setSelectedSubscription(subscription);
    setIsCancelModalActive(true);
  }, []);

  const onPauseSubscription = useCallback((subscription: ISubscription) => {
    trackPredefinedEvent('SUBSCRIPTION_PAUSE_SCREEN_VIEWED', {
      subscription_id: subscription.id,
      specialist_id: subscription.therapist_id,
    });
    setSelectedSubscription(subscription);
    setIsPauseModalActive(true);
  }, []);

  const onUnpauseSubscription = useCallback((subscription: ISubscription) => {
    trackPredefinedEvent('SUBSCRIPTION_RESTART_SCREEN_VIEWED', {
      subscription_id: subscription.id,
      specialist_id: subscription.therapist_id,
    });
    setSelectedSubscription(subscription);
    setIsUnpauseModalActive(true);
  }, []);

  const loadSubscriptionData = useCallback(
    async (event?: CustomEvent<RefresherEventDetail>) => {
      await dispatch(getFirestoreGlobalVariablesThunk());
      const localSubscriptions = await dispatch(fetchSubscriptions(user?.id)).unwrap();
      await dispatch(loadConsultationsThunk(user?.id));

      event?.detail?.complete();

      if (!localSubscriptions.length) {
        showToast({
          text: intl.formatMessage({
            id: 'subscriptionControl.noActiveSubscription',
            defaultMessage: 'Схоже що Ви не маєте активної підписки.',
          }),
          intercomArticleID: SUBSCRIPTION_WHAT_THERAPY_PLAN_IS,
        });
        history.back();
      }
    },
    [user?.id, intl, history]
  );

  useIonViewWillEnter(async () => {
    try {
      if (!user?.id) return;

      dispatch(resetOffset());
      dispatch(fetchChatRooms(user?.id));

      if (EURToUAHRate && subscriptions.loading === 'succeeded') return;

      setLoading(true);

      await loadSubscriptionData();
    } catch (error) {
      showToast({ error });
    } finally {
      setLoading(false);
    }
  }, [subscriptions.data]);

  return !loading ? (
    <IonPage>
      <PageHeader
        backChevron
        intercomId={SUBSCRIPTION_CONTROL}
        title={intl.formatMessage({
          id: 'settings.subscriptions',
          defaultMessage: 'Підписка на терапію',
        })}
        subtitle={true}
      />
      <IonContent>
        <Refresher onIonRefresh={loadSubscriptionData} disabled={false} />
        <Container>
          <SubscriptionCardContainer>
            {[...(subscriptions.data ?? [])]
              ?.sort((el) =>
                el.subscription_status === 'active' && !el.cancel_at_period_end && !el.pause_collection ? -1 : 0
              )
              .map((subscription) => (
                <FullWidthItem key={subscription.id}>
                  <SubscriptionCard
                    subscription={subscription}
                    onPauseClick={onPauseSubscription}
                    onCancelClick={openCancelFlowHandler}
                    onUnPauseClick={onUnpauseSubscription}
                  />
                </FullWidthItem>
              ))}
          </SubscriptionCardContainer>
        </Container>
      </IonContent>

      <CancelSubscriptionModal
        isOpen={isCancelModalActive}
        subscription={selectedSubscription}
        onDidDismiss={() => setIsCancelModalActive(false)}
        onPauseSubscription={() => selectedSubscription && onPauseSubscription(selectedSubscription)}
      />
      <PauseModal
        isOpen={isPauseModalActive}
        subscription={selectedSubscription}
        onDidDismiss={() => setIsPauseModalActive(false)}
      />
      <UnpauseModal
        isOpen={isUnpauseModalActive}
        subscription={selectedSubscription}
        onDidDismiss={() => setIsUnpauseModalActive(false)}
      />
    </IonPage>
  ) : (
    <IonPage>
      <PageHeader
        backChevron
        intercomId={123}
        title={intl.formatMessage({
          id: 'settings.subscriptions',
          defaultMessage: 'Підписка на терапію',
        })}
      />
      <IonContent>
        <Container>
          <SubscriptionSkeleton />
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default SubscriptionControl;
